import {
  Award,
  Bell,
  Calendar,
  CreditCard,
  MessageSquare,
  Receipt,
  ShieldCheck,
  Tag,
  Ticket,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import {
  notificationCategoryForType,
  userConfigurableCategories,
  type NotificationCategory,
} from '@tickethall/core';
import type { NotificationRow } from '@tickethall/core';

/**
 * Metadados de apresentação de uma notificação: ícone, para onde o clique vai
 * e qual texto do botão aparece.
 *
 * Antes desta página, o mesmo conhecimento estava em quatro cadeias de
 * `type.includes(...)` independentes — `getNotificationCategory` no sino,
 * `getNotificationIcon`/`getDestination`/`getActionLabel` na tela de
 * notificações. Elas discordavam entre si: o sino roteava pedido por
 * `data.order_id` (chave que nenhum emissor escreve) e a tela roteava revenda
 * para `/minha-conta` enquanto o sino mandava para `/notificacoes`. Um clique
 * no sino e o mesmo clique na lista levavam a lugares diferentes.
 *
 * Regra: o tipo decide a categoria (fonte: `NOTIFICATION_TYPE_CATEGORIES` no
 * core); a categoria decide ícone e botão; o payload decide o destino quando
 * houver link direto.
 */

export const INBOX_PATH = '/notificacoes';

// ============================================================
// Preferências por categoria
// ============================================================

export interface NotificationPrefMeta {
  category: NotificationCategory;
  icon: LucideIcon;
  /** Chave em `account.json` → `notifications.items.<category>`. */
  labelKey: string;
  descriptionKey: string;
  /** Usado se o locale ainda não tiver a chave — texto em pt-BR. */
  fallbackLabel: string;
  fallbackDescription: string;
}

const PREF_META: Record<NotificationCategory, NotificationPrefMeta> = {
  purchase: {
    category: 'purchase',
    icon: Receipt,
    labelKey: 'notifications.items.purchase.label',
    descriptionKey: 'notifications.items.purchase.description',
    fallbackLabel: 'Confirmação de compra',
    fallbackDescription: 'Receba quando sua compra for confirmada',
  },
  reminder: {
    category: 'reminder',
    icon: Calendar,
    labelKey: 'notifications.items.reminder.label',
    descriptionKey: 'notifications.items.reminder.description',
    fallbackLabel: 'Lembrete de evento',
    fallbackDescription: '24h e 1h antes do evento começar',
  },
  transfer: {
    category: 'transfer',
    icon: Ticket,
    labelKey: 'notifications.items.transfer.label',
    descriptionKey: 'notifications.items.transfer.description',
    fallbackLabel: 'Transferência de ingresso',
    fallbackDescription: 'Quando alguém transferir um ingresso para você',
  },
  refund: {
    category: 'refund',
    icon: CreditCard,
    labelKey: 'notifications.items.refund.label',
    descriptionKey: 'notifications.items.refund.description',
    fallbackLabel: 'Reembolso e cancelamento',
    fallbackDescription: 'Atualizações sobre reembolsos e cancelamentos',
  },
  message: {
    category: 'message',
    icon: MessageSquare,
    labelKey: 'notifications.items.message.label',
    descriptionKey: 'notifications.items.message.description',
    fallbackLabel: 'Mensagens do produtor',
    fallbackDescription: 'Respostas, avisos e comunicados sobre o evento',
  },
  promo: {
    category: 'promo',
    icon: Tag,
    labelKey: 'notifications.items.promo.label',
    descriptionKey: 'notifications.items.promo.description',
    fallbackLabel: 'Promoções e novidades',
    fallbackDescription: 'Descontos e eventos recomendados',
  },
  security: {
    category: 'security',
    icon: ShieldCheck,
    labelKey: 'notifications.items.security.label',
    descriptionKey: 'notifications.items.security.description',
    fallbackLabel: 'Segurança e privacidade',
    fallbackDescription: 'Alertas críticos da conta e pedidos de exclusão de dados',
  },
};

/**
 * Só as categorias que o usuário pode desligar. `security` fica de fora de
 * propósito: a UI do iOS trata essa categoria como forçada, e um toggle aqui
 * seria uma promessa que o servidor (`userAllowsPush`) não cumpre.
 */
export function configurableNotificationPrefs(): NotificationPrefMeta[] {
  return userConfigurableCategories().map((category) => PREF_META[category]);
}

// ============================================================
// Apresentação por categoria
// ============================================================

const CATEGORY_ICON: Record<NotificationCategory, LucideIcon> = {
  purchase: Receipt,
  reminder: Calendar,
  promo: Tag,
  transfer: Ticket,
  refund: CreditCard,
  message: MessageSquare,
  security: ShieldCheck,
};

/**
 * Tipos cujo ícone difere do da categoria.
 *
 * Só entram aqui os que a categoria descreve errado. Mensagens não entram
 * mais: `message` tem `MessageSquare` como ícone próprio.
 */
const TYPE_ICON: Record<string, LucideIcon> = {
  certificate_issued: Award,
  wallet_withdrawal_requested: Wallet,
  wallet_withdrawal_failed: Wallet,
};

export function notificationCategory(n: Pick<NotificationRow, 'type'>): NotificationCategory {
  return notificationCategoryForType(n.type);
}

export function notificationIcon(n: Pick<NotificationRow, 'type'>): LucideIcon {
  const type = (n.type ?? '').toLowerCase();
  const override = TYPE_ICON[type];
  if (override) return override;
  return CATEGORY_ICON[notificationCategoryForType(type)];
}

// ============================================================
// Destino
// ============================================================

/**
 * Lê uma chave do payload `data`.
 *
 * As Edge Functions escrevem em camelCase (`orderId`, `eventId`, `ticketId`).
 * As variantes em snake_case existem porque os gatilhos SQL e o iOS já usaram
 * esse formato — aceitar os dois evita que uma notificação antiga vire órfã
 * quando a convenção mudar de novo.
 */
function read(
  data: Record<string, unknown> | null,
  ...keys: string[]
): string | null {
  if (!data) return null;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

/**
 * Para onde o clique vai.
 *
 * A ordem é do mais específico para o menos: um `order_refunded` chega com
 * `orderId` **e** `eventSlug`, e o pedido é o destino que a pessoa quer.
 */
export function notificationDestination(n: Pick<NotificationRow, 'type' | 'data'>): string {
  const data = n.data ?? null;
  const type = (n.type ?? '').toLowerCase();

  const orderId = read(data, 'orderId', 'order_id');
  if (orderId) return `/pedido/${orderId}`;

  if (type === 'certificate_issued' || read(data, 'certificateId', 'certificate_id')) {
    return '/meus-certificados';
  }

  if (read(data, 'ticketId', 'ticket_id')) return '/meus-ingressos';

  const eventSlug = read(data, 'eventSlug', 'event_slug');
  if (eventSlug) return `/eventos/${eventSlug}`;

  // Sem link direto no payload. A caixa é o destino honesto: a notificação
  // continua legível lá, e o link "Ver todas" leva ao mesmo lugar.
  return INBOX_PATH;
}

/** Chave i18n (`account` → `notifications.actions.*`) do rótulo do botão. */
export function notificationActionKey(n: Pick<NotificationRow, 'type' | 'data'>): string {
  const type = (n.type ?? '').toLowerCase();
  const data = n.data ?? null;

  if (read(data, 'orderId', 'order_id')) return 'notifications.actions.viewOrder';
  if (type === 'certificate_issued' || read(data, 'certificateId', 'certificate_id')) {
    return 'notifications.actions.viewCertificates';
  }
  if (read(data, 'ticketId', 'ticket_id')) return 'notifications.actions.viewTickets';
  if (read(data, 'eventSlug', 'event_slug')) return 'notifications.actions.viewEvent';
  if (notificationCategoryForType(type) === 'security') return 'notifications.actions.openAccount';
  return 'notifications.actions.viewDetails';
}

/** Sino genérico — só usado quando a linha vier sem `type`. */
export const FALLBACK_ICON: LucideIcon = Bell;

// ============================================================
// Tempo
// ============================================================

const TIME_ZONE = 'America/Sao_Paulo';

/**
 * "agora", "há 3 min", "há 2 h", depois data.
 *
 * Havia duas cópias divergentes disto: o sino usava `date-fns` com locale
 * pt-BR fixo e timezone, a tela de notificações usava
 * `Intl.RelativeTimeFormat` sem timezone e com o literal "Agora" hardcoded.
 * As duas também divergiam no corte de 24 h.
 */
export function formatNotificationTime(dateStr: string | null, locale: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const intlLocale = locale === 'en' ? 'en-US' : 'pt-BR';
  const diffMs = Date.now() - date.getTime();

  // Notificação com timestamp no futuro (clock skew) não vira "em -3 min".
  if (diffMs < 60_000) {
    return new Intl.RelativeTimeFormat(intlLocale, { numeric: 'auto' }).format(0, 'second');
  }

  if (diffMs < 24 * 60 * 60 * 1000) {
    const rtf = new Intl.RelativeTimeFormat(intlLocale, { numeric: 'auto' });
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 60) return rtf.format(-minutes, 'minute');
    return rtf.format(-Math.floor(diffMs / 3_600_000), 'hour');
  }

  const now = new Date();
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(intlLocale, {
      day: '2-digit',
      month: 'short',
      timeZone: TIME_ZONE,
    });
  }

  return date.toLocaleDateString(intlLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: TIME_ZONE,
  });
}
